import { useState, useEffect, createContext } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material';
import CreateNoticeHeader from './CreateNoticeHeader';
import SelectNoticeTemplateDialog from './SelectNoticeTemplateDialog';
import NoticeFrame from './NoticeFrame';
import DisabledNoticeFrame from './DisabledNoticeFrame';
import { REASONS } from '../common/constants';
import { MESSAGES } from '../common/messages';
import axios from 'axios';
import { enqueueSnackbar } from 'notistack';
import dayjs from 'dayjs';

import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';

// 送信先選択ダイアログ、テンプレートから通告作成ダイアログと共有する値
export const Context = createContext();

const CreateNotice = (props) => {
  const { typography } = useTheme();
  const { loginUser } = props;

  // マスタデータ
  const [masterData, setMasterData] = useState([]);

  // ダイアログの開閉状態
  const [openSelectReceiver, setOpenSelectReceiver] = useState(false);
  const [openPreview, setOpenPreview] = useState({ isOpen: false, index: null }); // indexは対象の通告を表す。nullの場合は全ての通告。
  const [openSelectTemplateDialog, setOpenSelectTemplateDialog] = useState(false);

  // POSTで送信するデータ
  const [receivers, setReceivers] = useState([]);
  const [noticeArray, setNoticeArray] = useState([]);

  // 送信・プレビューボタンの状態
  const isDisable = () => {
    if (receivers.length === 0) {
      return true;
    }
    if (!noticeArray.some((notice) => notice.sentAt === undefined)) {
      return true;
    }
    const incompleteNotice = noticeArray.find((notice) =>
      notice.startStationCode === '' ||
      notice.endStationCode === '' ||
      (!notice.isImmediate && notice.delayMin === 0 && notice.delaySec === 0));
    if (incompleteNotice) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await axios.get('/v1/sections');
        setMasterData(data);
      } catch (err) { }
    };
    fetchData();
  }, []);

  // indexに指定された通告を送信する。indexがnullの場合は全ての通告を送信する。
  const handleSubmit = async (index) => {
    const receiversEmployeeNumber = receivers.map((user) => user.employeeNumber);
    const notices = [];
    const target = index === null ? noticeArray.filter((notice) => notice.sentAt === undefined) : [noticeArray[index]];
    target.map((notice) => {
      notices.push({
        id: notice.id,
        delaySecTime: notice.delayMin * 60 + notice.delaySec,
        sectionCode: notice.sectionCode,
        noticeCategoryCode: notice.noticeCategoryCode,
        noticeTypeCode: notice.noticeTypeCode,
        startStationCode: notice.startStationCode,
        endStationCode: notice.endStationCode,
        reasonCode: notice.reasonCode,
      });
    });
    try {
      const { data } = await axios.post('/v1/notices', { receiversEmployeeNumber, notices });
      enqueueSnackbar(MESSAGES.NOTICE_SUBMITTED);
      const newArray = structuredClone(noticeArray);
      if (index === null) {
        newArray.forEach((notice) => {
          if (notice.sentAt === undefined) {
            notice.sentAt = data.find((d) => d.id === notice.id).sentAt;
          }
        });
      } else {
        newArray[index].sentAt = data[0].sentAt;
      }
      setNoticeArray(newArray);
    } catch (err) {
    } finally {
      setOpenPreview({ isOpen: false, index });
    }
  };

  const handleAddNotice = (isNoNotice) => {
    let newNotice = {};
    if (isNoNotice) {
      newNotice = {
        sectionCode: '',
        sectionName: '',
        noticeCategoryCode: 0,
        noticeTypeCode: 0,
        startStationCode: '',
        endStationCode: '',
        reasonCode: REASONS.NULL.value,
        isImmediate: true,
        delayMin: 0,
        delaySec: 0,
      };
    } else {
      newNotice = structuredClone(noticeArray).slice(-1)[0];
      delete newNotice.sentAt;
    }
    newNotice.id = dayjs().valueOf();
    setNoticeArray([
      ...noticeArray,
      newNotice,
    ]);
  };

  // 通告追加・複製ボタン
  const AddNoticeButton = (props) => {
    const isNoNotice = props.noticeCount === 0;
    return (
      <Button variant="outlined"
        sx={{
          ...typography.body14bold, width: '240px', height: '44px',
          margin: '16px', backgroundColor: 'white', borderColor: 'divider', color: 'primary.light',
        }}
        startIcon={isNoNotice ? <AddIcon /> : <ContentCopyRoundedIcon />}
        onClick={() => handleAddNotice(isNoNotice)}
      >
        {isNoNotice ? '手入力で通告作成' : 'ひとつ上を複製して通告追加'}
      </Button>
    );
  };

  // テンプレートを複製した通告の作成ボタン
  const CreateFromTemplateButton = (props) => {
    return (
      <Button variant="outlined"
        sx={{
          ...typography.body14bold, width: '240px', height: '44px',
          marginLeft: '16px', backgroundColor: 'white', borderColor: 'divider', color: 'primary.light',
        }}
        startIcon={<AddIcon />}
        onClick={() => {
          setOpenSelectTemplateDialog(true);
        }}
      >
        {props.noticeCount === 0 ? 'テンプレートから通告作成' : 'テンプレートから通告追加'}
      </Button>
    );
  };

  AddNoticeButton.propTypes = {
    noticeCount: PropTypes.number,
  };

  CreateFromTemplateButton.propTypes = {
    noticeCount: PropTypes.number,
  };

  return (
    <>
      <Context.Provider
        value={{
          receivers, setReceivers,
          openSelectReceiver, setOpenSelectReceiver,
          openPreview, setOpenPreview,
          handleSubmit, isDisable,
          noticeArray, setNoticeArray,
          loginUser, masterData,
        }}
      >
        <CreateNoticeHeader />
        <SelectNoticeTemplateDialog
          open={openSelectTemplateDialog}
          setOpen={setOpenSelectTemplateDialog}
          masterData={masterData}
        />
        <Container>
          {noticeArray.map((notice, i) =>
            notice.sentAt ?
              <DisabledNoticeFrame key={notice.id} notice={notice} index={i} /> :
              <NoticeFrame key={notice.id} notice={notice} index={i} />,
          )}
          <CreateFromTemplateButton noticeCount={noticeArray.length} />
          <AddNoticeButton noticeCount={noticeArray.length} />
        </Container>
      </Context.Provider>
    </>
  );
};

export default CreateNotice;

CreateNotice.propTypes = {
  notice: PropTypes.object,
  loginUser: PropTypes.object,
};
